/**
 * Signaling utilities for WebRTC connection setup
 */

import QRCode from 'qrcode';
import type { SignalingData } from '../rtc/types.ts';

/**
 * Compress and encode signaling data for sharing
 */
export function encodeSignalingData(data: SignalingData): string {
  try {
    const jsonString = JSON.stringify(data);
    // Use btoa for base64 encoding (basic compression)
    return btoa(jsonString);
  } catch {
    throw new Error('Failed to encode signaling data');
  }
}

/**
 * Decode signaling data from shared string
 */
export function decodeSignalingData(encoded: string): SignalingData {
  try {
    const jsonString = atob(encoded);
    return JSON.parse(jsonString);
  } catch {
    throw new Error('Failed to decode signaling data');
  }
}

/**
 * Generate QR code for signaling data
 */
export async function generateQRCode(data: SignalingData): Promise<string> {
  try {
    const encoded = encodeSignalingData(data);
    const qrCodeDataURL = await QRCode.toDataURL(encoded, {
      errorCorrectionLevel: 'M',
      type: 'image/png',
      margin: 1,
      color: {
        dark: '#000000',
        light: '#FFFFFF',
      },
      width: 256,
    });
    return qrCodeDataURL;
  } catch {
    throw new Error('Failed to generate QR code');
  }
}

/**
 * Copy text to clipboard
 */
export async function copyToClipboard(text: string): Promise<void> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
    } else {
      // Fallback for non-secure contexts
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      document.execCommand('copy');
      textArea.remove();
    }
  } catch {
    throw new Error('Failed to copy to clipboard');
  }
}

/**
 * Read text from clipboard
 */
export async function readFromClipboard(): Promise<string> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      return await navigator.clipboard.readText();
    } else {
      throw new Error('Clipboard API not available');
    }
  } catch {
    throw new Error('Failed to read from clipboard');
  }
}

/**
 * Validate signaling data format
 */
export function validateSignalingData(data: unknown): data is SignalingData {
  if (typeof data !== 'object' || data === null) {
    return false;
  }

  const signalingData = data as SignalingData;

  // Must have either offer or answer
  if (!signalingData.offer && !signalingData.answer) {
    return false;
  }

  // Validate offer structure if present
  if (signalingData.offer) {
    if (
      typeof signalingData.offer.type !== 'string' ||
      typeof signalingData.offer.sdp !== 'string'
    ) {
      return false;
    }
  }

  // Validate answer structure if present
  if (signalingData.answer) {
    if (
      typeof signalingData.answer.type !== 'string' ||
      typeof signalingData.answer.sdp !== 'string'
    ) {
      return false;
    }
  }

  // Validate ICE candidates if present
  if (signalingData.iceCandidates) {
    if (!Array.isArray(signalingData.iceCandidates)) {
      return false;
    }
  }

  return true;
}
